import React, {
	createContext,
	ReactNode,
	useContext,
	useReducer,
} from 'react';

import {
	subtract,
	valueMinMax,
	zeroAndUp,
} from '../utils/utils';

export enum VideoMachineActionType {
	SetInitialized = 'SetInitialized',
	UpdateDuration = 'UpdateDuration',
	UpdateElapsed = 'UpdateElapsed',
	SetPlayingOnShotId = 'SetPlayingOnShotId'
}

/*
	It should be clear what this is since we could potentially be tracking a
	number of video states at the same time. Doing so may introduce
	performance issues, so a design that doesn't update a giant object or
	array of video states should be considered.

	Ticket reads (Green-1228):

	--------------------------------------------------------------------------
	This is for any shots with video. Only for single media file items.
	Slide deck with video: ignore for now.
	--------------------------------------------------------------------------

	This means we only want to track the elapsed time for a single video
	visible on canvas and not in a slide deck.
*/
type VideoMachineState = {
	isInitialized: boolean;
	isVideoSet: boolean;
	duration: number;
	elapsed: number;
	elapsedPercent: number;
	timeLeft: number;
	playingOnShotId: number;
};

type VideoMachineAction = {
	type: VideoMachineActionType;
	payload: unknown;
};

type VideoMachineProviderProps = {
	children: ReactNode;
};

type VideoMachineDispatch = (action: VideoMachineAction) => void;

type VideoMachineStateContext = {
	videoMachineState: VideoMachineState;
	videoMachineDispatch: VideoMachineDispatch;
};

type VideoMachineStateUpdate = (
	state: VideoMachineState,
	action: VideoMachineAction,
) => VideoMachineState;

type VideoMachineActionMap = Map<
	VideoMachineActionType,
	VideoMachineStateUpdate
>;

const defaultVideoMachineState: VideoMachineState = {
	isInitialized: false,
	isVideoSet: false,
	duration: 6,
	elapsed: 0,
	elapsedPercent: 0,
	timeLeft: 0,
	playingOnShotId: 0,
};

const VideoMachineContext = createContext<VideoMachineStateContext>({
	videoMachineState: defaultVideoMachineState,
	videoMachineDispatch: () => { },
});

// Using a method that should be familiar given how we set up Redux
const actions: VideoMachineActionMap = new Map([
	[
		VideoMachineActionType.SetInitialized,
		(state, action): VideoMachineState => {
			const isInitialized = action.payload as boolean;

			if (isInitialized) {
				return {
					...state,
					isInitialized,
				};
			}

			return defaultVideoMachineState;
		},
	],
	[
		VideoMachineActionType.UpdateDuration,
		(state, action): VideoMachineState => ({
			...state,
			isInitialized: true,
			// A boolean derived from `duration`.
			isVideoSet: !!(action.payload as number),
			timeLeft: zeroAndUp(subtract(
				action.payload,
				state.elapsed,
			)),
			duration: action.payload as number,
		}),
	],
	[
		VideoMachineActionType.UpdateElapsed,
		(state, action): VideoMachineState => ({
			...state,
			timeLeft: zeroAndUp(subtract(
				state.duration,
				action.payload,
			)),
			elapsed: valueMinMax(
				action.payload,
				0,
				state.duration,
			),
			elapsedPercent: valueMinMax(
				((action.payload as number) / state.duration) * 100,
				0,
				100,
			),
		}),
	],
	[
		VideoMachineActionType.SetPlayingOnShotId,
		(state, action): VideoMachineState => ({
			...state,
			playingOnShotId: action.payload as number,
		}),
	],
]);

function videoMachineReducer(
	state: VideoMachineState,
	action: VideoMachineAction,
): VideoMachineState {
	return actions.get(action.type)?.(state, action) ?? state;
}

function VideoMachineProvider({ children }: VideoMachineProviderProps) {
	const [videoMachineState, videoMachineDispatch] = useReducer(
		videoMachineReducer,
		defaultVideoMachineState,
	);
	const value = { videoMachineState, videoMachineDispatch };

	return (
		<VideoMachineContext.Provider value={value}>
			{children}
		</VideoMachineContext.Provider>
	);
}

// Makes it easy to use the video machine state in components
function useVideoMachine(): VideoMachineStateContext {
	const context = useContext(VideoMachineContext);
	return context;
}

export { useVideoMachine, VideoMachineProvider };
